import type { AceLevel } from '@/lib/aces-data';
import { cn } from '@/lib/utils';

type AceLevelTimelineProps = {
  levels: AceLevel[];
  className?: string;
};

export function AceLevelTimeline({ levels, className }: AceLevelTimelineProps) {
  if (!levels.length) return null;

  return (
    <div className={cn('relative', className)}>
      <div
        className="absolute left-[19px] top-3 bottom-3 w-px bg-gradient-to-b from-amber-300 via-indigo-300 to-violet-300 md:left-1/2 md:-translate-x-px"
        aria-hidden
      />

      <ol className="space-y-8">
        {levels.map((level, index) => {
          const isEven = index % 2 === 0;
          return (
            <li
              key={level.level}
              className={cn(
                'relative grid gap-4 md:grid-cols-2 md:gap-8',
                !isEven && 'md:[&>div:first-child]:order-2'
              )}
            >
              <div className={cn('md:pr-8', !isEven ? 'md:text-right' : 'md:col-start-1')}>
                <div
                  className={cn(
                    'inline-flex items-center gap-2',
                    !isEven && 'md:flex-row-reverse'
                  )}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-amber-300 bg-white font-poppins text-sm font-bold text-amber-700 shadow-sm">
                    {level.level}
                  </span>
                  <div className={cn(!isEven && 'md:text-right')}>
                    <p className="font-poppins text-xs font-semibold uppercase tracking-widest text-amber-600">
                      Nível {level.level}
                    </p>
                    <h3 className="font-poppins text-lg font-bold text-gray-900">{level.name}</h3>
                  </div>
                </div>
                <p className="mt-2 font-inter text-sm text-gray-600">{level.description}</p>
              </div>

              <div
                className={cn(
                  'rounded-2xl border border-gray-200 bg-gray-50/80 p-4',
                  isEven ? 'md:ml-8' : 'md:mr-8'
                )}
              >
                <p className="mb-2 font-poppins text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Benefícios
                </p>
                <ul className="space-y-1.5">
                  {level.perks.map((perk) => (
                    <li
                      key={perk}
                      className="flex items-start gap-2 font-inter text-sm text-gray-700"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                      {perk}
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
